import type { GridState, StateUpdatedEvent } from 'ag-grid-community';
import { isEqual, sortBy } from 'lodash-es';
import { useState, type ReactNode } from 'react';
import { useMixpanel } from '../../contexts/MixpanelContext';
import { useCallbackRef } from '../../hooks';
import { MixpanelEvent } from '../../tokens/mixpanel';
import { logger } from '../../utils';
import type { FormControlSizes } from '../Form';
import { ToggleGroup } from '../Form';
import { getAgGridColId, type Column } from './columns';
import { isColumn, type UseBlotterTableUtilitiesOutput } from './types';
import type { UseBlotterTableProps } from './useBlotterTable';

export const CUSTOM_BLOTTER_GROUPING = 'Custom';

export interface BlotterGroupingDefinition<AllowedGroupings extends string, GroupableColumns extends string> {
  key: AllowedGroupings;
  label: string;
  mobileLabel?: string;
  rowGroupColumns: GroupableColumns[];
  mixpanelEvent?: MixpanelEvent;
  buttonDataTestId?: string;
}

interface UseBlotterGroupingParams<AllowedGroupings extends string, GroupableColumns extends string> {
  /** Just a name for the blotter, used to identify it in a potential warning message. Can be anything. */
  blotterName: string;
  /** The initial columns of the blotter. Usually coming from usePersistedBlotterTable. */
  initialColumns?: UseBlotterTableProps<any>['columns'];
  /** The definitions of the groupings you want to support. */
  groupings: BlotterGroupingDefinition<AllowedGroupings, GroupableColumns>[];
  /** The function this hook uses to update blotter grouping state when columns change. Taken from the return object of useBlotterTable. */
  setRowGroupColumns: UseBlotterTableUtilitiesOutput<any>['setRowGroupColumns'];
  /** Allow unselecting all groupings, effectively removing any grouping in the blotter. Default: false. */
  allowNoGrouping?: boolean;

  /** Size of the grouping buttons returned. Accepts numeric size enums used by existing blotter callers. */
  size?: FormControlSizes | number;
  /** Allow grouping buttons to wrap onto multiple rows. Default: false. */
  wrap?: boolean;
  /** Use mobile-specific button labels when available. Default: false. */
  useMobileLabels?: boolean;

  /** TreeData from GridOptions - as treeData views manage their groupings via autoColumnGroupDef Data Path, the functionality
   * of this hook is not needed. Default: false.
   */
  treeData?: boolean;
}

interface UseBlotterGroupingOutput {
  /** Function to connect to ag-grid's stateUpdated event handling. */
  onStateUpdated: (event: StateUpdatedEvent) => void;
  /** Function to call when blotter columns change. */
  updateGroupingOnColumnsChanged?: (columns: Column[]) => void;
  groupingToggleButtons: ReactNode;
}

/** Handle Non-tree-based Row Group Toggling */
export const useBlotterGroupingToggling = <AllowedGroupings extends string, GroupableColumns extends string>({
  blotterName,
  initialColumns,
  groupings,
  setRowGroupColumns,
  allowNoGrouping,
  treeData,
  size,
  wrap,
  useMobileLabels,
}: UseBlotterGroupingParams<AllowedGroupings, GroupableColumns>): UseBlotterGroupingOutput => {
  const mixpanel = useMixpanel();

  // if treeData is true, the rest of this hook is not needed as the autoGroupColumnDef will be used
  const columnsToGroupingMethod: typeof columnsToGrouping = treeData
    ? () => {
        return undefined;
      }
    : columnsToGrouping;

  // This state can be undefined -- which means that we are either in a treeData view or in some unresolved grouping state.
  // The user resolves this by clicking on any of the grouping toggle buttons.
  // Shouldn't happen -- and if it does, we don't want to do anything to accidentally trigger an infinite re-render. Just leave be.
  const [selectedGrouping, setSelectedGrouping] = useState<AllowedGroupings | undefined>(() => {
    if (initialColumns == null) {
      return undefined;
    }

    return columnsToGroupingMethod(initialColumns.filter(isColumn), groupings, blotterName);
  });

  const handleGroupingColIdsChanged = useCallbackRef((groupingColIds: string[]) => {
    setSelectedGrouping(groupingColIdsToGrouping(groupingColIds, groupings, blotterName));
  });

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    handleGroupingColIdsChanged(event.state.rowGroup?.groupColIds ?? []);
  });

  const handleColumnsChanged = useCallbackRef((columns: Column[]) => {
    const groupingColIds = columnsToGroupingColIds(columns);
    handleGroupingColIdsChanged(groupingColIds);
  });

  const handleToggleButtonClicked = useCallbackRef(
    (grouping: BlotterGroupingDefinition<AllowedGroupings, GroupableColumns>) => {
      if (selectedGrouping === grouping.key && allowNoGrouping) {
        // If we're clicking the already selected grouping, and we allow "un-grouping", we remove all grouping
        setRowGroupColumns([]);
        mixpanel.track(MixpanelEvent.GroupingRemoved);
      } else {
        if (grouping.mixpanelEvent) {
          mixpanel.track(grouping.mixpanelEvent);
        }

        setRowGroupColumns(grouping.rowGroupColumns);
      }
    }
  );
  const handleGroupingChange = useCallbackRef((key: AllowedGroupings) => {
    const grouping = groupings.find(group => group.key === key);
    if (grouping != null) {
      handleToggleButtonClicked(grouping);
    }
  });

  if (treeData) {
    return {
      onStateUpdated: handleGridStateUpdated,
      updateGroupingOnColumnsChanged: undefined,
      groupingToggleButtons: null,
    };
  }

  return {
    onStateUpdated: handleGridStateUpdated,
    updateGroupingOnColumnsChanged: handleColumnsChanged,
    groupingToggleButtons: (
      <ToggleGroup<AllowedGroupings>
        options={groupings.map(grouping => ({
          value: grouping.key,
          displayName: useMobileLabels ? (grouping.mobileLabel ?? grouping.label) : grouping.label,
          testId: grouping.buttonDataTestId,
        }))}
        value={selectedGrouping}
        onChange={handleGroupingChange}
        boxProps={{ w: 'auto', flexWrap: wrap ? 'wrap' : undefined }}
        size={size as FormControlSizes | undefined}
      />
    ),
  };
};

export function stateToGrouping<AllowedGroupings extends string, GroupableColumns extends string>(
  state: GridState,
  groupings: UseBlotterGroupingParams<AllowedGroupings, GroupableColumns>['groupings'],
  blotterName: string
): AllowedGroupings | undefined {
  const groupingColIds = state.rowGroup?.groupColIds ?? [];
  return groupingColIdsToGrouping(groupingColIds, groupings, blotterName);
}

export function columnsToGrouping<AllowedGroupings extends string, GroupableColumns extends string>(
  columns: Column<any>[],
  groupings: UseBlotterGroupingParams<AllowedGroupings, GroupableColumns>['groupings'],
  blotterName: string
): AllowedGroupings | undefined {
  const groupingColIds = columnsToGroupingColIds(columns);
  return groupingColIdsToGrouping(groupingColIds, groupings, blotterName);
}

function columnsToGroupingColIds(columns: Column<any>[]): string[] {
  // Find all columns which are used in the grouping, then sort by their grouping index.
  // Initial row-group fields are used by column definitions before AG Grid emits live state.
  return sortBy(columns.filter(isRowGroupColumn), getRowGroupIndex).map(getAgGridColId);
}

function isRowGroupColumn(column: Column<any>): boolean {
  const hasLiveGrouping = column.rowGroup === true || column.rowGroupIndex != null;
  const hasLiveGroupingState = column.rowGroup != null || column.rowGroupIndex != null;

  if (hasLiveGroupingState) {
    return hasLiveGrouping;
  }

  return column.initialRowGroup === true || column.initialRowGroupIndex != null;
}

function getRowGroupIndex(column: Column<any>): number | undefined {
  if (column.rowGroup === true || column.rowGroupIndex != null) {
    return column.rowGroupIndex ?? column.initialRowGroupIndex;
  }

  return column.initialRowGroupIndex;
}

function groupingColIdsToGrouping<AllowedGroupings extends string, GroupableColumns extends string>(
  groupingColIds: string[],
  groupings: UseBlotterGroupingParams<AllowedGroupings, GroupableColumns>['groupings'],
  blotterName: string
): AllowedGroupings | undefined {
  const groupingBeingUsed = groupings.find(grouping => isEqual(groupingColIds, grouping.rowGroupColumns))?.key;

  if (groupingBeingUsed == null) {
    const maybeCustomGrouping = groupings.find(grouping => grouping.key === CUSTOM_BLOTTER_GROUPING);
    if (maybeCustomGrouping) {
      // We couldn't find any matching grouping spec, but we do support fully custom groupings, so we return that we are doing Custom grouping.
      return maybeCustomGrouping.key;
    }

    // We weren't able to find anything... shouldn't happen, something is wrong
    logger.warn(`Unable to resolve grouped colIds in blotter: ${blotterName}`, {
      extra: {
        colIds: groupingColIds,
      },
    });
  }

  return groupingBeingUsed;
}
